import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { CartCouponComponent } from '@spartacus/cart/base/components';
import { ActiveCartFacade, Cart, CartVoucherFacade } from '@spartacus/cart/base/root';
import {
  AuthService,
  CustomerCoupon,
  CustomerCouponService,
  FeatureConfigService,
  OCC_USER_ID_ANONYMOUS,
  TranslatePipe,
  UserIdService,
} from '@spartacus/core';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { AppliedCouponsComponent } from './applied-coupons/applied-coupons.component';

@Component({
  selector: 'cx-cart-coupon',
  templateUrl: './cart-coupon.component.html',
  styleUrls: ['./cart-coupon.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    NgClass,
    MatFormField,
    MatLabel,
    MatInput,
    AppliedCouponsComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class ShimanoCartCouponComponent extends CartCouponComponent implements OnInit, OnDestroy {
  MAX_CUSTOMER_COUPON_PAGE = 100;
  form: FormGroup;
  cartIsLoading$: Observable<boolean>;
  submitDisabled$: Observable<boolean>;
  cart$: Observable<Cart>;
  cartId: string;
  applicableCoupons: CustomerCoupon[];
  filteredCoupons: CustomerCoupon[];
  couponInputDataHasValue: boolean = false;
  couponInputData: any;

  protected ignoreCloseEvent = false;

  protected subscription = new Subscription();

  couponBoxIsActive = false;

  showSrp = false;
  srpSubscription$: Subscription;

  constructor(
    protected activeCartService: ActiveCartFacade,
    private authService: AuthService,
    protected cartVoucherService: CartVoucherFacade,
    protected formBuilder: FormBuilder,
    private componentService: ComponentService,
    private cdr: ChangeDetectorRef,
    private languageService: ShimanoLanguageService,
    protected customerCouponService: CustomerCouponService,
    protected featureConfig: FeatureConfigService,
    private storageService: StorageService,
    private userIdService?: UserIdService
  ) {
    super(cartVoucherService, formBuilder, customerCouponService, activeCartService);
  }

  ngOnInit() {
    if (this.featureConfig && this.featureConfig.isLevel('1.5')) {
      this.cart$ = combineLatest([this.activeCartService.getActive(), this.userIdService.getUserId()]).pipe(
        tap(([cart, userId]: [Cart, string]) => {
          this.cartId = userId === OCC_USER_ID_ANONYMOUS ? cart.guid : cart.code;
          this.getApplicableCustomerCoupons(cart, []);
        }),
        map(([cart]: [Cart, string]) => cart)
      );
    }
    //TODO(issue:#5971) Deprecated since 1.5
    else {
      this.cart$ = combineLatest([this.activeCartService.getActive(), this.userIdService.getUserId()]).pipe(
        tap(
          ([cart, userId]: [Cart, string]) => (this.cartId = userId === OCC_USER_ID_ANONYMOUS ? cart.guid : cart.code)
        ),
        map(([cart]: [Cart, string]) => cart)
      );
    }
    //TODO(issue:#5971) Deprecated since 1.5

    this.cartIsLoading$ = this.activeCartService.isStable().pipe(map((loaded) => !loaded));

    this.cartVoucherService.resetAddVoucherProcessingState();

    this.form = this.formBuilder.group({
      couponCode: ['', [Validators.required]],
    });

    this.submitDisabled$ = combineLatest([
      this.cartIsLoading$,
      this.form.valueChanges.pipe(
        startWith(true),
        map(() => this.form.valid)
      ),
      this.cartVoucherService.getAddVoucherResultLoading(),
      this.componentService.srpToggle$.pipe(
        startWith(false),
        map((checked) => checked)
      ),
    ]).pipe(
      map(
        ([cartIsLoading, btnEnabled, addVoucherIsLoading, srpToggleChecked]) =>
          cartIsLoading || !btnEnabled || addVoucherIsLoading || srpToggleChecked
      )
    );

    this.subscription.add(
      this.cartVoucherService.getAddVoucherResultSuccess().subscribe((success) => {
        this.onSuccess(success);
      })
    );

    this.subscription.add(
      this.cartVoucherService.getAddVoucherResultError().subscribe((error) => {
        this.onError(error);
      })
    );

    this.getInitSrpToggleValue();

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });
  }

  protected onError(error: boolean) {
    if (error) {
      this.customerCouponService.loadCustomerCoupons(this.MAX_CUSTOMER_COUPON_PAGE);
      this.cartVoucherService.resetAddVoucherProcessingState();
    }
  }

  onSuccess(success: boolean) {
    if (success) {
      this.form.reset();
      this.inActive(null);
      this.cartVoucherService.resetAddVoucherProcessingState();
    }
  }

  protected getApplicableCustomerCoupons(cart: Cart, coupons: CustomerCoupon[]): void {
    this.applicableCoupons = coupons || [];
    if (cart.appliedVouchers) {
      cart.appliedVouchers.forEach((appliedVoucher) => {
        this.applicableCoupons = this.applicableCoupons.filter((coupon) => coupon.couponId !== appliedVoucher.code);
      });
    }
    this.filteredCoupons = this.applicableCoupons;
  }

  applyVoucher(): void {
    if (!this.form.valid) {
      this.form.markAsTouched();
      return;
    }
    this.cartVoucherService.addVoucher(this.form.value.couponCode, this.cartId);
  }

  applyCustomerCoupon(couponId: string): void {
    this.cartVoucherService.addVoucher(couponId, this.cartId);
    this.couponBoxIsActive = false;
  }

  change = (value: string) => {
    this.couponInputDataHasValue = value ? true : false;
  };

  click() {
    this.couponInputDataHasValue = true;
  }
  inActive = (value: string) => {
    if (this.isEmptyInputValue(value)) {
      this.form.reset();
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].setErrors(null);
      });
    }
    this.couponInputDataHasValue = value ? true : false;
  };

  addPlaceholder(): void {
    /*do nothing so it will retain the placeholder*/
  }

  open(): void {
    this.filteredCoupons = this.applicableCoupons;
    if (this.applicableCoupons.length > 0) {
      this.couponBoxIsActive = true;
    }
  }

  close(event: UIEvent): void {
    if (!this.ignoreCloseEvent) {
      this.couponBoxIsActive = false;
      if (event && event.target) {
        (event.target as HTMLElement).blur();
      }
    }
    this.ignoreCloseEvent = false;
  }

  disableClose(): void {
    this.ignoreCloseEvent = true;
  }

  isEmptyInputValue(value: any): boolean {
    return value == null || value.length === 0;
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  get activeLanguage$(): Observable<string> {
    return this.languageService.getActive();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.srpSubscription$) {
      this.srpSubscription$.unsubscribe();
    }
    this.cartVoucherService.resetAddVoucherProcessingState();
  }
}
